import { useCallback, useState } from 'react'
import { navigate } from 'gatsby'
import type {
  ProductItem,
  AnalyticsItem,
} from 'src/components/restructure/product/types/productItem'
import type { Product } from 'src/components/restructure/product/types/product'
import { useOptionalProductContextSelector } from 'src/components/restructure/product/contexts'
import { useCheckout } from 'src/sdk/checkout/useCheckout'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { getProductQueryObject } from 'src/utils/amplitude/useProductObject'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import trackAddToCart from 'src/utils/dynamicYield/trackAddToCart'
import { useCartFeedback } from 'src/components/contexts/CartFeedbackContext'
import { buildInsuranceCustomData } from 'src/components/common/SourceTracker/utils/buildCustomData'
import { useToastContext } from 'src/components/ui/ToastMessage/ToastContext'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'
import { isPreSale } from 'src/utils/pdp/isPreSale'

import { useProductQuantity } from '../../ProductQuantity/hooks/useProductQuantity'

export type Item = {
  id: string
  productId?: string
  isPreSale?: boolean
  seller: string
  quantity: number
  index: number
  price: number
  amplitude?: AnalyticsItem['amplitude']
}

type UseBuyButtonProps = {
  propCurrentSku?: ProductItem
}

export function useBuyButton(
  { propCurrentSku }: UseBuyButtonProps = {} as UseBuyButtonProps
) {
  const [openModal, setOpenModal] = useState(false)
  const [openPreSaleModal, setOpenPreSaleModal] = useState(false)
  const [pendingAction, setPendingAction] = useState<{
    item: Item
    type: 'addToCart' | 'buyNow'
  } | null>(null)

  const { pageData } = useGlobalContext()

  const contextSku = useOptionalProductContextSelector((ctx) => ctx.currentSku)

  const currentSku = contextSku ?? propCurrentSku

  const productObject = getProductQueryObject(currentSku)

  const standardObject = getStandardObject(pageData)
  const { maxValue } = useProductQuantity()
  const { sendToast } = useToastContext()

  const { openCartFeedback } = useCartFeedback()
  const { addItems, getItems, orderForm, clearCart, addCustomData } =
    useCheckout()

  const cartItems = getItems()

  const validateGiftCardRestrictions = useCallback(
    (item: Item, type: 'addToCart' | 'buyNow' = 'addToCart') => {
      const isGiftCard = (productId: string | undefined) =>
        productId === '11113'

      const currentProductIsGiftCard = isGiftCard(item.productId)
      const cartContainsGiftCard = cartItems?.some((cartItem) =>
        isGiftCard(cartItem.productId)
      )

      const cartContainsNonGiftCard = cartItems?.some(
        (cartItem) => !isGiftCard(cartItem.productId)
      )

      if (
        (cartContainsNonGiftCard && currentProductIsGiftCard) ||
        (cartContainsGiftCard && !currentProductIsGiftCard)
      ) {
        setPendingAction({ item, type })
        setOpenModal(true)

        return true
      }

      return false
    },
    [cartItems]
  )

  const validatePreSaleRestrictions = useCallback(
    (item: Item, type: 'addToCart' | 'buyNow' = 'addToCart') => {
      const currentProductIsPreSale = item.isPreSale
      const orderFormItems = orderForm?.items ?? []
      const cartContainsPreSaleItem = orderFormItems.some((orderformItem) =>
        isPreSale(orderformItem.preSaleDate ?? '')
      )

      const cartContainsNonPreSaleItems = orderFormItems.some(
        (orderformItem) => !isPreSale(orderformItem.preSaleDate ?? '')
      )

      if (
        (cartContainsNonPreSaleItems && currentProductIsPreSale) ||
        (cartContainsPreSaleItem && !currentProductIsPreSale)
      ) {
        setPendingAction({ item, type })
        setOpenPreSaleModal(true)

        return true
      }

      return false
    },
    [orderForm.items]
  )

  const handleClearCartAndAdd = useCallback(
    async (onAfterAdd?: () => void) => {
      if (!pendingAction) {
        return
      }

      // Pega todos os vínculos de insurance ANTES de limpar o carrinho
      const insuranceApp = orderForm.customData?.customApps?.find(
        (app) => app.id === 'insurance'
      )

      let insuranceData: Array<Record<string, string>> = []

      if (insuranceApp?.fields?.data) {
        try {
          const parsed = JSON.parse(insuranceApp.fields.data as string)

          insuranceData = Array.isArray(parsed) ? parsed : []
        } catch {
          insuranceData = []
        }
      }

      // Se houver vínculos de insurance, limpa ANTES de limpar o carrinho
      if (insuranceData.length > 0) {
        try {
          // Remove todos os vínculos de insurance enviando array vazio
          await addCustomData(
            buildInsuranceCustomData(JSON.stringify([])),
            true
          )
          // Aguarda um momento para garantir que foi processado
          await new Promise((resolve) => {
            setTimeout(resolve, 300)
          })
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Erro ao limpar insurance customData:', error)
        }
      }

      // Agora limpa o carrinho
      await clearCart()
      // Adiciona o novo item
      await addItems([pendingAction.item])

      setPendingAction(null)
      setOpenModal(false)

      // Se há callback, executa ele (para abrir modal de insurance por exemplo)
      if (onAfterAdd) {
        onAfterAdd()

        return
      }

      if (pendingAction.type === 'buyNow') {
        navigate('/checkout/profile')
      } else {
        openCartFeedback([pendingAction.item.id])
      }
    },
    [
      pendingAction,
      clearCart,
      addItems,
      orderForm.customData,
      addCustomData,
      openCartFeedback,
    ]
  )

  const addToCart = useCallback(
    async ({
      item,
      type = 'addToCart',
      origin,
      eventData,
      eventSource,
      shouldOpenFeedback = true,
      skipAmplitudeEvent = false,
    }: {
      item: Item | Item[]
      type?: 'addToCart' | 'buyNow'
      origin?: string
      shouldCheckSpecification?: boolean
      eventData?: Record<string, unknown>
      eventSource?: string
      shouldOpenFeedback?: boolean
      skipAmplitudeEvent?: boolean
    }): Promise<boolean> => {
      if (!item) {
        return false
      }

      localStorage.removeItem('lastClickedList')

      const addSource =
        origin ??
        (type === 'addToCart'
          ? 'Botão adicionar ao carrinho'
          : 'Botão comprar agora')

      const itemsToAdd = Array.isArray(item) ? item : [item]
      const productDetails = itemsToAdd
        .map((itemToAdd) => {
          if (!itemToAdd.amplitude) {
            return null
          }

          return getProductQueryObject({
            ...itemToAdd.amplitude,
            quantity: itemToAdd.quantity,
            purchase_price: itemToAdd.price,
            purchase_value: itemToAdd.price * itemToAdd.quantity,
          })
        })
        .filter((detail): detail is Record<string, unknown> => !!detail)

      for (const itemToAdd of itemsToAdd) {
        if (validateGiftCardRestrictions(itemToAdd, type)) {
          return false
        }

        if (validatePreSaleRestrictions(itemToAdd, type)) {
          return false
        }

        const totalQuantityForThisProduct = orderForm.items
          ?.filter((cartItem) => itemToAdd.id === cartItem.id)
          .reduce((acc, curr) => acc + curr.quantity, 0)

        if (totalQuantityForThisProduct >= maxValue) {
          sendToast({
            text: 'O limite máximo é de 30 itens por produto.',
            variant: 'error',
          })

          return false
        }
      }

      if (!skipAmplitudeEvent) {
        // Payload is built synchronously (same data, same timing as before)
        // so the deferred dispatch below can't observe stale state — only
        // the `amplitude.track` call itself is pushed off the click
        // handler's synchronous path, via queueMicrotask.
        const addToCartEventData = {
          ...standardObject,
          'product details':
            productDetails.length > 0 ? productDetails : [productObject],
          'add location': pageData.currentPageType,
          'product viewed source': eventSource ?? addSource,
          ...(eventData && { ...eventData }),
          'add to cart source': eventSource ?? addSource,
        }

        queueMicrotask(() => {
          dispatchAmplitudeEvent({
            eventName: 'Product Added To Cart',
            eventData: addToCartEventData,
          })
        })
      }

      trackAddToCart({ cartItems, itemsToAdd })

      await addItems(itemsToAdd)

      if (type === 'addToCart') {
        if (shouldOpenFeedback) {
          openCartFeedback(itemsToAdd.map((addedItem) => addedItem.id))
        }

        return true
      }

      navigate('/checkout/profile')

      return true
    },
    [
      standardObject,
      productObject,
      pageData.currentPageType,
      cartItems,
      addItems,
      validateGiftCardRestrictions,
      validatePreSaleRestrictions,
      orderForm.items,
      maxValue,
      sendToast,
      openCartFeedback,
    ]
  )

  const mapperCartItem = (productToMap: Product, item: ProductItem): Item => {
    return {
      id: item.itemId,
      productId: productToMap.productId,
      seller: item.sellerDefault.sellerId,
      quantity: 1,
      index: 0,
      price: item.offerDefault?.price ?? 0,
    }
  }

  return {
    addToCart,
    mapperCartItem,
    openModal,
    setOpenModal,
    openPreSaleModal,
    setOpenPreSaleModal,
    validateGiftCardRestrictions,
    handleClearCartAndAdd,
    pendingAction,
    setPendingAction,
  }
}
